import type { FC } from "react";
import { useBoolean } from "usehooks-ts";

import {
  INSTRUCTOR_FORUM_USER_GUIDE_URL,
  RELEASE_NOTES_URL,
  STUDENT_FORUM_USER_GUIDE_URL,
} from "../../utils/urls";

import { BugReportModal } from "components/header/BugReportModal";
import { useIntercomWithDefaults } from "components/intercom/use-intercom-with-defaults";
import { t } from "i18n/i18n";
import { Menu, MenuDivider, MenuItem } from "mds/components/Menu";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { CircleQuestionIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch } from "store/index";
import { viewStateActions } from "store/slices/view";
import { isIntercomEnabled } from "utils/waffle-flags";

export const HelpCenterMenu: FC = () => {
  const dispatch = useAppDispatch();
  const { showIntercom } = useIntercomWithDefaults();
  const { canAuthorSection } = useCapabilities();
  const {
    value: bugReportModalIsShown,
    setTrue: showBugReportModal,
    setFalse: hideBugReportModal,
  } = useBoolean(false);

  const handleContactSupport = () => {
    if (!isIntercomEnabled()) return;

    showIntercom();
  };

  const MenuButton = (
    <div>
      <SquareIconButton aria-label={t("help_center.title")} kind="secondary" size="s">
        <CircleQuestionIcon />
      </SquareIconButton>
    </div>
  );

  return (
    <>
      <Menu menuButton={MenuButton}>
        {isIntercomEnabled() && (
          <>
            <MenuItem
              eventName="Help Center - Contact support"
              title={t("help_center.contact_support")}
              onClick={handleContactSupport}
            >
              {t("help_center.contact_support")}
            </MenuItem>
            <MenuDivider />
          </>
        )}
        <MenuItem
          eventName="Help Center - User guide"
          title={t("help_center.forum_user_guide")}
          onClick={() =>
            window.open(
              canAuthorSection ? INSTRUCTOR_FORUM_USER_GUIDE_URL : STUDENT_FORUM_USER_GUIDE_URL,
              "_blank",
              "noopener",
            )
          }
        >
          {t("help_center.forum_user_guide")}
        </MenuItem>
        <MenuItem
          eventName="Help Center - Release notes"
          title={t("help_center.release_notes")}
          onClick={() => window.open(RELEASE_NOTES_URL, "_blank", "noopener")}
        >
          {t("help_center.release_notes")}
        </MenuItem>
        <MenuDivider />
        <MenuItem
          eventName="Help Center - Keyboard shortcuts"
          title={t("keyboard_shortcuts.menu_item")}
          onClick={() => dispatch(viewStateActions.setKeyboardShortcutsDialogOpen(true))}
        >
          {t("keyboard_shortcuts.menu_item")}
        </MenuItem>
        <MenuItem
          eventName="Help Center - Report a problem"
          title={t("help_center.report_problem")}
          onClick={showBugReportModal}
        >
          {t("help_center.report_problem")}
        </MenuItem>
      </Menu>

      <BugReportModal open={bugReportModalIsShown} onClose={hideBugReportModal} />
    </>
  );
};
